import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { NavBar, Button, ActionSheet, Toast, Skeleton } from 'antd-mobile';
import { CameraOutline, MessageOutline, ExclamationCircleFill } from 'antd-mobile-icons';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { getNavBarContainerStyle } from '../../../../utils/deviceUtils';
import {
    GET_CONTRACT_CUSTOMER_MESSAGE_TEMPLATE,
    GetContractCustomerMessageTemplateResponse,
} from '../../graphql/queries/getContractCustomerMessageTemplate';
import {
    GET_DRIVER_SMS,
    DriverSmsResponse,
    DriverSmsVariables,
    DriverSmsThreadDetail,
} from './graphql/queries/getDriverSms';
import { sendSms } from '../../graphql/mutations/sendSms';
import { updateIsReadStatus } from './graphql/mutations/updateIsReadStatus';
import { DEFAULT_MESSAGE_TEMPLATES } from '../../../constants/defaultMessageTemplates';
import { 
    GET_ORDER_CUSTOMER_COMPANY_NAME,
    GetOrderCustomerCompanyNameResponse,
    GetOrderCustomerCompanyNameVariables
} from '../../graphql/queries/getOrderCustomerCompanyName';
import {logger} from "@sentry/react";

// 获取当前语言类型
const getCurrentLanguageType = (language: string) => {
    const isChinese = language === 'zh' || language === 'zh-CN';
    const isSpanish = language === 'es' || language === 'es-ES';
    return { isChinese, isSpanish };
};

interface ChatMessage {
    id: string;
    content: string;
    type: 'text' | 'image';
    sender: 'driver' | 'customer';
    timestamp: number;
    imageUrl?: string;
    status?: 'sending' | 'sent' | 'failed'; // 消息发送状态
}


const ChatPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { i18n, t } = useTranslation();
    const { stopNumber, fullName , tracking: trackingNumber, subStopNumber, driverId,driverName,orderAddressId } = location.state || {};
    const [templateSheetVisible, setTemplateSheetVisible] = useState(false);
    const [sending, setSending] = useState(false);
    const [localMessages, setLocalMessages] = useState<ChatMessage[]>([]); // 本地消息（发送后立即显示）
    const [initialLoadComplete, setInitialLoadComplete] = useState(false); // 标记初始加载是否完成
    const [isAtBottom, setIsAtBottom] = useState(true); // 用户是否在聊天底部
    const [showNewMessageButton, setShowNewMessageButton] = useState(false); // 是否显示新消息提示按钮
    const [previousMessageCount, setPreviousMessageCount] = useState(0); // 上一次消息数量
    const [isComponentMounted, setIsComponentMounted] = useState(false); // 标记组件是否真正挂载
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);

    // 构造页面标题，如果subStopNumber存在，则将其拼接到stopNumber后面
    const pageTitle = stopNumber && fullName 
        ? `${t('pages.delivery.delivering.stop')} ${subStopNumber ? `${stopNumber}-${subStopNumber}` : stopNumber} - ${fullName}`
        : t('pages.delivery.delivering.chat');

    // 从后端获取模板消息
    const { data: templateData, loading: templateLoading, error: templateError } = useQuery<GetContractCustomerMessageTemplateResponse>(
        GET_CONTRACT_CUSTOMER_MESSAGE_TEMPLATE,
        {
            fetchPolicy: 'cache-and-network',
            errorPolicy: 'all'
        }
    );

    // 获取客户公司名称
    const { data: companyData } = useQuery<GetOrderCustomerCompanyNameResponse, GetOrderCustomerCompanyNameVariables>(
        GET_ORDER_CUSTOMER_COMPANY_NAME,
        {
            variables: { trackingNumber: trackingNumber || '' },
            skip: !trackingNumber,
            fetchPolicy: 'cache-first'
        }
    );

    // 从后端获取短信详情（每次进入页面都查询一次，并持续轮询以实时显示客户回复）
    const { data: smsData, loading: smsLoading, refetch: refetchSms } = useQuery<
        DriverSmsResponse
    >(
        GET_DRIVER_SMS,
        {
            variables: {
                driverId: driverId || 0,
                trackingNumber: trackingNumber || '',
                language: i18n.language
            },
            skip: !driverId || !trackingNumber,
            fetchPolicy: 'network-only', // 每次都从网络获取最新数据
            errorPolicy: 'all',
            pollInterval: 15000, // 轮询
            notifyOnNetworkStatusChange: true
        }
    );

    // 只在初始加载时显示骨架屏
    const showInitialLoading = !initialLoadComplete && (templateLoading || smsLoading);

    // 当初始数据加载完成时，标记初始加载完成
    useEffect(() => {
        if (!initialLoadComplete && !templateLoading && !smsLoading && (templateData || templateError) && smsData) {
            setInitialLoadComplete(true);
        }
    }, [templateLoading, smsLoading, templateData, templateError, smsData, initialLoadComplete]);

    // 检测用户是否在聊天底部
    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;
            setIsAtBottom(isNearBottom);
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, []);

    // 将短信详情转换为 ChatMessage 格式（从服务器数据转换）
    const serverMessages: ChatMessage[] = useMemo(() => {
        const details = smsData?.getDriverSms?.details ?? [];
        const recordId = smsData?.getDriverSms?.details[0].recordId ?? 'unknown'

        return details.map((detail: DriverSmsThreadDetail, index: number) => {
            const timestampSeconds = detail.createdAt ?? Math.floor(Date.now() / 1000);
            const timestamp = timestampSeconds * 1000;

            // 根据detail中的status值设置ChatMessage的status
            let status: 'sent' | 'failed' | 'sending' | undefined ;
            if (detail.status === "2") {
                status = 'failed';
            } else if (detail.status === "1") {
                status = 'sent';
            }else {
                status = 'sending';
            }

            return {
                id: `sms-${recordId}-${timestampSeconds}-${index}`,
                content: detail.message,
                type: 'text' as const,
                sender: detail.roleType === 1 ? ('driver' as const) : ('customer' as const),
                timestamp,
                status
            };
        });
    }, [smsData?.getDriverSms?.details]);

    // 合并服务器消息和本地消息（发送后立即显示）
    const messages: ChatMessage[] = useMemo(() => {
        // 合并服务器消息和本地消息
        const allMessages = [...serverMessages, ...localMessages];
        
        // 去重：如果本地消息的内容已经在服务器消息中，则移除本地消息
        const uniqueMessages = allMessages.filter((msg, index, self) => {
            // 先通过 ID 去重
            if (index !== self.findIndex(m => m.id === msg.id)) {
                return false;
            }
            // 如果是本地消息，检查服务器消息中是否有相同内容的消息（30秒内）
            if (msg.id.startsWith('local-')) {
                const timeThreshold = 30000; // 30秒
                return !serverMessages.some(serverMsg => 
                    serverMsg.content === msg.content &&
                    serverMsg.sender === msg.sender &&
                    Math.abs(serverMsg.timestamp - msg.timestamp) < timeThreshold
                );
            }
            return true;
        });
        
        return uniqueMessages.sort((a, b) => a.timestamp - b.timestamp);
    }, [serverMessages, localMessages]);

    // 根据当前语言获取模板消息列表（显示文案和实际发送的detailMessage）
    const templateMessages = useMemo(() => {
        const { isChinese, isSpanish } = getCurrentLanguageType(i18n.language);
        // 如果查询失败或数据为空，使用默认模板
        const templates = templateError || !templateData?.getContractCustomerMessageTemplate || templateData.getContractCustomerMessageTemplate.length === 0
            ? DEFAULT_MESSAGE_TEMPLATES
            : templateData.getContractCustomerMessageTemplate;

        return templates.map(template => {
            let displayText: string;
            if (isChinese) {
                displayText = template.chinese;
            } else if (isSpanish) {
                displayText = template.spanish;
            } else {
                displayText = template.english;
            }
            
            return {
                template: template,
                displayText: displayText,
                detailMessage: template.detailMessage
            };
        });
    }, [templateData?.getContractCustomerMessageTemplate, templateError, i18n.language]);

    // 滚动到底部
    const scrollToBottom = (smooth: boolean = true) => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTo({
                top: messagesContainerRef.current.scrollHeight,
                behavior: smooth ? 'smooth' : 'auto'
            });
        }
    };

    // 点击新消息提示按钮时滚动到底部
    const handleNewMessageClick = () => {
        scrollToBottom(true);
        setShowNewMessageButton(false);
    };

    // 监听用户是否滚动到底部，如果是则隐藏新消息按钮
    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!container) return;

        let timeoutId: NodeJS.Timeout;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;
            
            // 清除之前的定时器
            clearTimeout(timeoutId);
            
            // 设置一个短暂的延迟来检查位置，避免滚动过程中的误判
            timeoutId = setTimeout(() => {
                if (isNearBottom) {
                    setShowNewMessageButton(false);
                }
            }, 100);
        };

        container.addEventListener('scroll', handleScroll);
        return () => {
            container.removeEventListener('scroll', handleScroll);
            clearTimeout(timeoutId);
        };
    }, []);

    // 当消息更新时，根据用户位置决定是否自动滚动或显示提示按钮
    useEffect(() => {
        if (messagesContainerRef.current && messages.length > 0) {
            // 检查是否有新消息
            const hasNewMessages = messages.length > previousMessageCount;
            
            // 更新消息计数
            if (messages.length !== previousMessageCount) {
                setPreviousMessageCount(messages.length);
            }

            // 如果用户在底部或者这是初始加载，则自动滚动到底部
            if (!initialLoadComplete) {
                setTimeout(() => {
                    scrollToBottom(true);
                }, 0);
            } else if (hasNewMessages) {
                // 检查最新消息是否由司机发送
                const latestMessage = messages[messages.length - 1];
                const isDriverMessage = latestMessage.sender === 'driver';
                
                // 如果是司机自己发送的消息，则不显示新消息提示按钮
                if (isDriverMessage) {
                    scrollToBottom(true);
                    setShowNewMessageButton(false);
                    return;
                }
                
                // 检查用户是否非常接近底部（例如，在底部的200像素范围内）
                const container = messagesContainerRef.current;
                if (container) {
                    const { scrollTop, scrollHeight, clientHeight } = container;
                    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
                    
                    // 如果距离底部很近（小于200像素），自动滚动到底部
                    if (distanceFromBottom <= 200) {
                        setTimeout(() => {
                            scrollToBottom(true);
                        }, 0);
                    } else {
                        // 只有在有新消息且用户不在底部且距离较远时才显示新消息提示按钮
                        setShowNewMessageButton(true);
                    }
                }
            }
        }
    }, [messages, initialLoadComplete, previousMessageCount]);

    // 处理返回
    const handleBack = () => {
        navigate(-1);
    };

    // 处理模板选择并发送短信
    const handleTemplateSelect = async (detailMessage: string) => {
        setTemplateSheetVisible(false);

        // 替换模板中的占位符
        let processedMessage = detailMessage;
        if (trackingNumber) {
            // 替换 {tracking number} 占位符（包括大括号）
            processedMessage = processedMessage.replace(/{tracking number}/g, trackingNumber);
        }
        
        // 替换 {company} 占位符（包括大括号）
        const companyName = companyData?.getOrderCustomerCompanyNameByTrackingNumber;
        if (companyName) {
            const lowerCaseName = companyName.toLowerCase();
            if (lowerCaseName.includes('temu')) {
                // 如果包含 "tmeu"，则展示 "tmeu"
                processedMessage = processedMessage.replace(/{company}/g, 'temu');
            } else if (lowerCaseName.includes('tiktok')) {
                // 如果包含 "tiktok"，则展示 "tiktok"
                processedMessage = processedMessage.replace(/{company}/g, 'tiktok');
            } else {
                // 移除 from {company} 部分
                processedMessage = processedMessage.replace(/ from \{company}/g, '');
            }
        } else {
            // 移除 from {company} 部分
            processedMessage = processedMessage.replace(/ from \{company}/g, '');
        }

        setSending(true);
        
        // 立即将消息添加到本地消息列表（发送中状态）

        const newMessage: ChatMessage = {
            id: `local-${Date.now()}-${Math.random()}`,
            content: processedMessage, // 使用处理后的消息
            type: 'text',
            sender: 'driver',
            timestamp: Date.now(),
            status: 'sending'
        };
        
        setLocalMessages(prev => [...prev, newMessage]);
        
        try {
            // 调用发送短信接口
            const result = await sendSms({
                message: processedMessage, // 使用处理后的消息
                stopNumber: stopNumber ? parseInt(stopNumber, 10) : 0,
                fullName: fullName || '',
                subStopNumber: subStopNumber || '',
                driverId: driverId ? parseInt(driverId.toString(), 10) : 0,
                trackingNumber: trackingNumber,
                orderAddressId: orderAddressId ? parseInt(orderAddressId.toString(), 10) : 0,
                driverName: driverName || '',
            });

            if (result.success) {
                // 发送成功，更新消息状态
                setLocalMessages(prev => 
                    prev.map(msg => 
                        msg.id === newMessage.id 
                            ? { ...msg, status: 'sent' as const }
                            : msg
                    )
                );
                
                // 发送成功后，重新查询以获取最新消息（包括刚发送的消息）
                refetchSms();
                scrollToBottom(true);
            } else {
                // 发送失败，更新消息状态并显示错误提示
                setLocalMessages(prev => 
                    prev.map(msg => 
                        msg.id === newMessage.id 
                            ? { ...msg, status: 'failed' as const }
                            : msg
                    )
                );

            }
        } catch (error:any) {
            console.error('Failed to send SMS:', error);
            // 发送失败，更新消息状态并显示错误提示
            setLocalMessages(prev => 
                prev.map(msg => 
                    msg.id === newMessage.id 
                        ? { ...msg, status: 'failed' as const }
                        : msg
                )
            );

            if(error.networkError){
                Toast.show({
                    content: t('pages.delivery.delivering.smsSendFailed'),
                    position: 'bottom'
                });
            }
        } finally {
            setSending(false);
        }
    };

    // 渲染加载状态
    const renderLoading = () => (
        <div className="px-3 py-4 space-y-4">
            <Skeleton.Title animated />
            <Skeleton.Paragraph lineCount={2} animated />
            <Skeleton.Title animated />
            <Skeleton.Paragraph lineCount={3} animated />
            <Skeleton.Title animated />
            <Skeleton.Paragraph lineCount={1} animated />
        </div>
    );

    // 渲染聊天内容
    const renderChatContent = () => {
        // 只在初始加载时显示骨架屏
        if (showInitialLoading) {
            return renderLoading();
        }

        if (messages.length === 0) {
            return (
                <div className="flex items-center justify-center h-full text-gray-400">
                    {t('pages.delivery.delivering.noMessages')}
                </div>
            );
        }

        return (
            <div className="px-3 py-4 space-y-3">
                {messages.map((message) => (
                    <div
                        key={message.id}
                        className={`flex ${message.sender === 'driver' ? 'justify-end' : 'justify-start'}`}
                    >
                        <div
                            className={`max-w-[75%] rounded-2xl px-4 py-2.5 shadow-sm ${
                                message.sender === 'driver'
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-green-500 text-white'
                            }`}
                            style={{
                                borderRadius: message.sender === 'driver' 
                                    ? '18px 18px 4px 18px' 
                                    : '18px 18px 18px 4px'
                            }}
                        >
                            <div className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                                {message.content}
                            </div>
                            <div className="flex items-center justify-end gap-1.5 mt-1.5">
                                {message.status === 'failed' && message.sender === 'driver' && (
                                    <ExclamationCircleFill 
                                        fontSize={14} 
                                        style={{ color: '#ef4444' }} 
                                    />
                                )}
                                {message.status === 'sending' && message.sender === 'driver' && (
                                    <div className="w-2 h-2 rounded-full bg-blue-200 animate-pulse"></div>
                                )}
                                <div
                                    className={`text-xs ${
                                        message.sender === 'driver'
                                            ? 'text-blue-100'
                                            : 'text-green-100'
                                    }`}
                                >
                                    {new Date(message.timestamp).toLocaleTimeString([], { 
                                        hour: '2-digit', 
                                        minute: '2-digit' 
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
                <div ref={messagesEndRef} />
            </div>
        );
    };

    // 当组件挂载和卸载时设置标记
    useEffect(() => {
        // 组件真正挂载
        setIsComponentMounted(true);
        
        // 组件将要卸载
        return () => {
            setIsComponentMounted(false);
        };
    }, []);

    // 当页面加载时，更新消息为已读状态
    useEffect(() => {
        const markMessagesAsRead = async () => {
            try {
                // 调用更新已读状态的接口，传入driverId和trackingNumber
                if (driverId && trackingNumber) {
                    await updateIsReadStatus(driverId, trackingNumber);
                }
            } catch (error) {
                console.error('Failed to update message read status:', error);
            }
        };

        // 只有当组件真正挂载、初始加载完成且有必要的数据时才执行
        if (isComponentMounted && initialLoadComplete && smsData && driverId && trackingNumber) {
            markMessagesAsRead();
        }
    }, [isComponentMounted, initialLoadComplete, smsData, driverId, trackingNumber]);

    return (
        <div className="flex flex-col h-screen bg-white">
            {/* 导航栏 - 顶部固定 */}
            <div {...getNavBarContainerStyle()} className="flex-shrink-0 bg-white border-b border-gray-200 shadow-sm">
                <NavBar
                    onBack={handleBack}
                    backIcon={<ArrowLeftOutlined />}
                >
                    <span className="text-gray-800 font-medium">{pageTitle}</span>
                </NavBar>
            </div>

            {/* 聊天记录列表 - 可滚动区域 */}
            <div 
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto bg-gray-50 relative"
                style={{ 
                    WebkitOverflowScrolling: 'touch',
                    minHeight: 0
                }}
            >
                
                {renderChatContent()}
            </div>
            
            {/* 新消息提示按钮 */}
            {showNewMessageButton && (
                <div className="fixed bottom-24 right-0 z-10">
                    <button
                        onClick={handleNewMessageClick}
                        className="flex items-center justify-center bg-white text-green-500 py-2 pl-4 pr-3 shadow-lg border border-green-500 hover:bg-green-50 transition-colors"
                        style={{ 
                            borderTopLeftRadius: '20px',
                            borderBottomLeftRadius: '20px',
                            borderTopRightRadius: '0',
                            borderBottomRightRadius: '0'
                        }}
                    >
                        <div className="flex items-center justify-center mr-2">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ color: '#10b981' }}>
                                <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        <span className="text-sm font-medium whitespace-nowrap">{t('pages.delivery.delivering.youHaveNewMessages')}</span>
                    </button>
                </div>
            )}

            {/* 发送信息框 - 固定在底部 */}
            <div className="flex-none flex-shrink-0 border-t border-gray-200 bg-white safe-area-bottom">
                <div className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                        {/* 模板按钮 - 占据大部分空间 */}
                        <Button
                            onClick={() => setTemplateSheetVisible(true)}
                            disabled={sending || showInitialLoading}
                            block
                            style={{ 
                                flex: 1,
                                backgroundColor: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '20px',
                                height: '44px'
                            }}
                        >
                            <div className="flex items-center justify-center gap-2">
                                <MessageOutline fontSize={20} style={{ color: 'white' }} />
                                <span style={{ color: 'white', fontWeight: 500 }}>
                                    {t('pages.delivery.delivering.sendTemplate')}
                                </span>
                            </div>
                        </Button>

                    </div>
                </div>
            </div>

            {/* 模板选择 ActionSheet */}
            <ActionSheet
                visible={templateSheetVisible && !showInitialLoading}
                actions={templateMessages.map((templateItem, index) => ({
                    text: templateItem.displayText,
                    key: index.toString(),
                    onClick: () => handleTemplateSelect(templateItem.detailMessage)
                }))}
                onClose={() => setTemplateSheetVisible(false)}
                cancelText={t('pages.delivery.delivering.cancel')}

            />
        </div>
    );
};

export default ChatPage;